import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SportsBioIcon from "@/components/SportsBioIcon";
import { useAuth } from "@/contexts/AuthContext";
import { isWebPlatform } from "@/lib/platform";

/**
 * Root route ("/"). Runs the animated splash (logo → Netflix-style
 * "SportsBio" reveal → hold), and only once the animation's `done` phase
 * is reached AND the Supabase session probe has resolved does it route:
 *   - no session                        → /auth
 *   - session, no profile row / no role → /auth  (ghost session — see below)
 *   - session, onboarding incomplete    → /player/onboarding or /scout/onboarding
 *   - session, fully onboarded          → /player/home or /scout/find
 *
 * So a returning user still lands on their home screen quickly — the
 * animation plays once, in parallel with the session probe.
 *
 * Ghost-session note: a session-bearing user with `profile.role` null is
 * a broken state (mid-signup pending-blob lost, profile row missing,
 * transient RLS / network hiccup on loadProfile). We previously routed
 * these to /role-select which silently re-attributed the account to a
 * fresh persona — wrong for existing users. The route now goes to /auth
 * so the user can re-authenticate cleanly. /role-select is no longer
 * reachable from any normal flow; see RoleSelection.tsx for the kept-
 * but-unreachable note.
 */
type Phase = "logo" | "name" | "done";

const SplashScreen = () => {
  const navigate = useNavigate();
  const { user, profile, loading, sessionAuthenticated } = useAuth();
  const [phase, setPhase] = useState<Phase>("logo");

  // Animation timeline — independent of the auth probe.
  //   0ms    logo in
  //   500ms  name slides in from both sides (600ms each)
  //   1800ms hold (500ms)
  //   2300ms done → route
  useEffect(() => {
    const t1 = window.setTimeout(() => setPhase("name"), 500);
    const t2 = window.setTimeout(() => setPhase("done"), 2300);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  // Routing fires only when BOTH the animation has finished AND the auth
  // state is resolved.
  useEffect(() => {
    if (phase !== "done" || loading) return;

    if (!user) {
      // Web: marketing pricing page is the first surface a new academy
      // sees. They can tap Sign In there to reach /auth.
      // Native: skip pricing — the persona picker on /auth is the
      // entry point for player / scout / academy / parent.
      navigate(isWebPlatform() ? "/pricing" : "/auth", { replace: true });
      return;
    }
    // WEB ONLY. Persisted Supabase session, but the operator hasn't
    // signed in within this browser tab session. Force them through
    // /auth so they actively re-authenticate (Google click or password
    // submit) before any protected route renders. sessionStorage is
    // wiped on tab close, so every reopen lands here — by design for a
    // browser, which may be a shared machine.
    //
    // On native sessionAuthenticated is always true (see AuthContext), so
    // a returning user goes straight to their home screen below.
    if (!sessionAuthenticated) {
      navigate("/auth", { replace: true });
      return;
    }
    if (!profile?.role) {
      // Ghost session — see header comment. Route to /auth so the user
      // re-authenticates instead of being silently re-asked to pick a
      // persona for an account that already exists.
      navigate("/auth", { replace: true });
      return;
    }
    // Academy + parent personas added in later prompts — route them
    // directly here so signed-in academies hitting / on web don't
    // bounce through the platform guards on /player or /scout.
    // Academy onboarding state lives on the academies row, not on
    // profiles, so a still-onboarding academy will be re-routed by
    // RequireAuth when AcademyLayout mounts.
    if (profile.role === "academy") {
      navigate("/academy/home", { replace: true });
      return;
    }
    if (profile.role === "parent") {
      navigate(isWebPlatform() ? "/parent" : "/parent/home", { replace: true });
      return;
    }
    if (!profile.onboarding_complete) {
      navigate(
        profile.role === "scout" ? "/scout/onboarding" : "/player/onboarding",
        { replace: true }
      );
      return;
    }
    navigate(profile.role === "scout" ? "/scout/find" : "/player/home", { replace: true });
  }, [phase, loading, user, profile, sessionAuthenticated, navigate]);

  const nameVisible = phase === "name" || phase === "done";

  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute w-96 h-96 rounded-full bg-primary/10 blur-[120px] animate-pulse-ring" />

      <motion.div
        className="flex flex-col items-center gap-6 z-10"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Logo — counter-rotating pair so the outer/inner appear to lock
            while the whole thing pulses. Centered above the name. */}
        <motion.div
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 2, ease: "linear", repeat: Infinity }}
        >
          <motion.div
            animate={{ rotate: [360, 0] }}
            transition={{ duration: 2, ease: "linear", repeat: Infinity }}
          >
            <SportsBioIcon size={96} />
          </motion.div>
        </motion.div>

        {/* Netflix-style name reveal. "Sports" from the left, "Bio" from the
            right — both meet at center. The row is overflow-hidden so the
            glyphs don't "show" off-screen before they're animated in. */}
        <div className="flex items-center justify-center overflow-hidden">
          <motion.span
            className="font-heading text-4xl font-extrabold text-gradient-green"
            initial={{ x: -120, opacity: 0 }}
            animate={nameVisible ? { x: 0, opacity: 1 } : { x: -120, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            Sports
          </motion.span>
          <motion.span
            className="font-heading text-4xl font-extrabold text-foreground"
            initial={{ x: 120, opacity: 0 }}
            animate={nameVisible ? { x: 0, opacity: 1 } : { x: 120, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            Bio
          </motion.span>
        </div>
      </motion.div>
    </div>
  );
};

export default SplashScreen;
